import React, { useCallback } from 'react';
import type { TerminalClause, CompoundClause } from '@atlaskit/jql-ast';
import type { TriggerProps } from '@atlaskit/popup';
import { Text } from '@atlaskit/primitives/compiled';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { useIntl } from '@atlassian/jira-intl';
import { FixedWidthPopupComponent } from '@atlassian/jira-jql-builder-basic-picker/src/ui/popup-container/index.tsx';
import removeFieldClauseFromJql from '@atlassian/jira-jql-utils/src/utils/remove-field-clause.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import PickerTrigger from '../../../../common/ui/picker-trigger/main.tsx';
import { useAST } from '../../../../controllers/ast/index.tsx';
import {
	useShouldRenderToParent,
	useVerticalMode,
} from '../../../../controllers/feature-context.tsx';
import messages from './messages.tsx';

type Props = {
	activePopupKey: string;
	clause: TerminalClause | CompoundClause | undefined;
	fieldDescription: string | null;
	fieldId: string;
	fieldLabel: string | undefined;
	onClose: () => void;
	onOpen: () => void;
	onClear: (onClearJqlClause: string | undefined) => void;
	onSwitchToJql?: () => void;
};

export const UnsupportedPickerWrapper = ({
	activePopupKey,
	clause,
	fieldId,
	fieldLabel,
	fieldDescription,
	onClose,
	onOpen,
	onClear,
	onSwitchToJql,
}: Props): React.JSX.Element => {
	const isOpen = activePopupKey === fieldId;
	const handleOnTriggerClick = useCallback(
		() => (isOpen ? onClose() : onOpen()),
		[isOpen, onClose, onOpen],
	);
	const [ast] = useAST();
	const { formatMessage } = useIntl();
	const shouldRenderToParent = useShouldRenderToParent();
	const verticalMode = useVerticalMode();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onSwitchToJqlWrapper = useCallback(() => {
		if (onSwitchToJql) {
			onSwitchToJql();
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			});

			fireUIAnalytics(analyticsEvent, 'jqlBuilderBasicUnsupportedField', {});
		}
	}, [createAnalyticsEvent, onSwitchToJql]);

	return (
		<Popup
			key={fieldId}
			placement="bottom-start"
			isOpen={isOpen}
			onClose={onClose}
			popupComponent={FixedWidthPopupComponent}
			shouldRenderToParent={shouldRenderToParent}
			messageId="jql-builder-basic.ui.jql-editor.field.unsupported-picker-wrapper.popup"
			messageType="transactional"
			trigger={(triggerProps: TriggerProps) => (
				<PickerTrigger
					{...triggerProps}
					isInvalid={false}
					isSelected={isOpen}
					hasValues={!!clause}
					onClick={handleOnTriggerClick}
					onClear={() => onClear(clause ? removeFieldClauseFromJql(ast, clause) : undefined)}
					testId={`jql-builder-basic.ui.jql-editor.picker.filter-button.${fieldId}`}
					fieldDescription={fieldDescription}
				>
					<Text color="inherit" weight="medium">
						{clause || verticalMode ? <Text as="strong">{fieldLabel}</Text> : fieldLabel}
					</Text>
				</PickerTrigger>
			)}
			content={() => (
				<SectionMessage
					actions={
						onSwitchToJql ? (
							<SectionMessageAction onClick={onSwitchToJqlWrapper}>
								{formatMessage(messages.switchToJql)}
							</SectionMessageAction>
						) : undefined
					}
				>
					<Text as="p">
						{formatMessage(messages.messageToCustomer, {
							fieldLabel,
							b: (chunks: React.ReactNode[]): JSX.Element => <b>{chunks}</b>,
						})}
					</Text>
				</SectionMessage>
			)}
		/>
	);
};

export default UnsupportedPickerWrapper;
